import { useQuery } from '@tanstack/react-query';
import type { InsightsTimeSeriesBucket } from '@brand24-original/llm-types';
import { GET_INSIGHTS_TIMESERIES } from '../../query/insights/keys';
import { getInsightsTimeSeries } from '../../services/insights/InsightsStatsService';
import { ChatType } from '../../../types/monitor/ChatType';
import { PeriodType } from '../../../types/prompt/PeriodType';

export interface InsightsTimeSeriesParams {
  projectId: number;
  chatType: ChatType;
  period: PeriodType;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterChatTypes?: string[];
  /**
   * The analyzed brand (project.firmName). Key-only: the backend reads it from the project, this just
   * forces a refetch when the ChangeKeywordBox switches it.
   */
  brand?: string;
}

export const useGetInsightsTimeSeries = (params: InsightsTimeSeriesParams | null) => {
  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<InsightsTimeSeriesBucket[]>({
    queryKey: [
      GET_INSIGHTS_TIMESERIES,
      params?.projectId,
      params?.chatType,
      params?.period,
      fromTimestamp,
      toTimestamp,
      params?.monitorPromptIds,
      params?.filterChatTypes,
      params?.brand,
    ],
    // `enabled` gates on params/from/to, so the assertions below cannot fire at runtime.
    queryFn: () =>
      getInsightsTimeSeries(
        params!.projectId,
        params!.chatType,
        params!.period,
        params!.from!,
        params!.to!,
        params!.monitorPromptIds,
        params!.filterChatTypes
      ),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled:
      !!params && !!params.from && !!params.to && !!params.chatType && !!params.period && !!params.projectId,
  });

  return {
    insightsTimeSeries: query.data ?? null,
    // isPending, not isLoading — patrz komentarz w UseGetInsightsOverview.
    isLoading: query.isPending,
    isError: query.isError,
  };
};
